import React, { useState, useEffect, useMemo } from 'react';
import { 
  Apple, 
  Wine, 
  Package, 
  Coffee, 
  Leaf, 
  Droplet, 
  Recycle, 
  Banana, 
  Beef, 
  Box, 
  Milk, 
  Layers 
} from 'lucide-react';
import axiosInstance from '@/api/axios';
import { useTranslation } from 'react-i18next';

const SubclassCardView = ({ 
  id, 
  dayvalue, 
  datevalue = [{ startDate: null, endDate: null, key: 'selection' }],
  isCustomDateRange,
  wasteweight = 0,
  totalwasteproduceddata = 0,
  className = ""
}) => {
  const { t } = useTranslation();
  
  // --- STATE ---
  const [wasteData, setWasteData] = useState([]);
  const [wasteTypeWeights, setWasteTypeWeights] = useState({});
  const [subclassData, setSubclassData] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayMode, setDisplayMode] = useState('kg'); // 'kg' or 'percentage'
  const [selectedCategory, setSelectedCategory] = useState('All');

  // --- ICONS ---
  const getIcon = (subclassName) => {
    const name = subclassName.toLowerCase();
    
    if (name.includes('banana')) return <Banana className="w-8 h-8" />;
    if (name.includes('apple')) return <Apple className="w-8 h-8" />;
    if (name.includes('bottle') || name.includes('glass')) return <Wine className="w-8 h-8" />;
    if (name.includes('can') || name.includes('metal')) return <Recycle className="w-8 h-8" />;
    if (name.includes('paper') || name.includes('cardboard') || name.includes('box')) return <Package className="w-8 h-8" />;
    if (name.includes('coffee')) return <Coffee className="w-8 h-8" />;
    if (name.includes('organic') || name.includes('mandarin') || name.includes('orange')) return <Leaf className="w-8 h-8" />;
    if (name.includes('bag') || name.includes('plastic') || name.includes('wrapper')) return <Droplet className="w-8 h-8" />;
    if (name.includes('carton') || name.includes('juice') || name.includes('drink')) return <Milk className="w-8 h-8" />;
    if (name.includes('meat') || name.includes('food')) return <Beef className="w-8 h-8" />;
    if (name.includes('container')) return <Box className="w-8 h-8" />;
    
    return <Recycle className="w-8 h-8" />;
  };

  const getColorClass = (index) => {
    const colors = [
      'bg-blue-100', 'bg-emerald-100', 'bg-rose-100', 'bg-cyan-100',
      'bg-purple-100', 'bg-amber-100', 'bg-yellow-100', 'bg-pink-100',
      'bg-indigo-100', 'bg-orange-100', 'bg-teal-100', 'bg-lime-100'
    ];
    return colors[index % colors.length];
  };

  const getCardSize = (index) => {
    // Standard size when filtering to avoid layout jumps
    if (selectedCategory !== 'All') return 'col-span-2 row-span-1 md:col-span-1 md:row-span-1';
    
    // Bento grid layout only for 'All' view
    if (index === 0) return 'col-span-2 row-span-2 md:col-span-2 md:row-span-2';
    if (index === 1 || index === 2) return 'col-span-2 row-span-1 md:col-span-2 md:row-span-1';
    if (index === 6) return 'col-span-2 row-span-1 md:col-span-2 md:row-span-1';
    return 'col-span-1 row-span-1';
  };

  // --- NORMALIZATION ---
  const shouldFilterWasteType = (wasteType) => {
    if (!wasteType) return true;
    const normalized = wasteType.toLowerCase().trim();
    return normalized === 'nothing' || normalized === '';
  };

  const normalizeAndCombineWasteTypes = (name) => {
    if (!name) return '';
    const normalized = name.toLowerCase().replace(/[_-]/g, '').trim();
    
    if (normalized === 'nothing') return '';
    
    // Normalize keys to ensure 'Paper' matches 'PaperCardboard' etc.
    const combinedMap = {
        'pmd': 'PMD',
        'residual': t('residuallabel') || 'Residual',
        'rest': t('residuallabel') || 'Residual',
        'papercardboard': t('paperlabel') || 'Paper/Cardboard',
        'paper': t('paperlabel') || 'Paper/Cardboard', 
        'organic': t('organiclabel') || 'Organic',
        'glass': 'Glass'
    };

    return combinedMap[normalized] ||
        name.replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
            .replace(/[_-]/g, ' ')
            .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
            .replace(/\s+/g, ' ')
            .trim();
  };

  const normalizeAndFormatSubclassName = (name) => {
    if (!name) return '';
    const transformations = {
      'plasticbottle': t('plasticBottle') || 'Plastic Bottle',
      'metalcan': t('metalCan') || 'Metal Can',
      'plasticbag': t('plasticBag') || 'Plastic Bag',
      'plasticbagcolor': t('plasticBagColor') || 'Plastic Bag Color',
      'organicmandarin': t('organicMandarin') || 'Organic Mandarin',
      'paperbox': t('paperBox') || 'Paper Box',
      'papercarton': t('paperCarton') || 'Paper Carton',
      'papercolor': t('paperColor') || 'Paper Color',
      'papertissue': t('paperTissue') || 'Paper Tissue',
      'paperoffice': t('paperOffice') || 'Paper Office',
      'plasticbagwrapper': t('plasticBagWrapper') || 'Plastic Wrapper',
      'plasticcontainer': t('plasticContainer') || 'Plastic Container',
      'drinkcarton': t('drinkCarton') || 'Drink Carton',
      'juicepackage': t('juicePackage') || 'Juice Package',
      'rubbergloves': t('rubberGloves') || 'Rubber Gloves',
      'banana': t('banana') || 'Banana',
      'apple': t('apple') || 'Apple',
      'papercoffeecup': t('paperCoffeeCup') || 'Coffee Cup',
      'plasticbagsilver': t('plasticBagSilver') || 'Silver Bag'
    };
    const lowerName = name.toLowerCase().replace(/[_-]/g, '');
    return transformations[lowerName] ||
      name.replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
        .replace(/[_-]/g, ' ')
        .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
        .trim();
  };

  // --- API 1: FETCH CATEGORY WEIGHTS ---
  useEffect(() => {
    const fetchWasteTypeWeights = async () => {
        try {
            let binIdentifiers = Array.isArray(id) ? id : (typeof id === 'string' && id.includes(',') ? id.split(',') : [id]);
            const bins = binIdentifiers.map(b => String(b).trim()).join(',');

            const timeRangeMap = { 'day': 'Day', 'week': 'Week', 'month': 'Month', '3months': '3Month', '6months': '6Month', 'year': 'Year' };
            const normalizedDayValue = dayvalue.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
            const timeRange = timeRangeMap[normalizedDayValue] || 'Month';

            const currentMonth = new Date().getMonth() + 1;
            let season = (currentMonth >= 3 && currentMonth <= 5) ? 'spring' : (currentMonth >= 6 && currentMonth <= 8) ? 'summer' : (currentMonth >= 9 && currentMonth <= 11) ? 'autumn' : 'winter';

            const params = { bins, timeRange, season };

            if (isCustomDateRange && datevalue[0]?.startDate && datevalue[0]?.endDate) {
                const sDate = datevalue[0].startDate instanceof Date ? datevalue[0].startDate : new Date(datevalue[0].startDate);
                const eDate = datevalue[0].endDate instanceof Date ? datevalue[0].endDate : new Date(datevalue[0].endDate);
                if (!isNaN(sDate) && !isNaN(eDate)) {
                    params.start_date = sDate.toISOString();
                    params.end_date = eDate.toISOString();
                }
            }

            const response = await axiosInstance.get('/others_page_waste_composition', { params });

            if (response.data) {
                const weights = {};
                
                // Map API keys to Normalized Keys
                const paperKey = t('paperlabel') || 'Paper/Cardboard';
                if (response.data.paper !== undefined) weights[paperKey] = response.data.paper;
                
                const pmdKey = 'PMD';
                if (response.data.pmd !== undefined) weights[pmdKey] = response.data.pmd;
                
                const residualKey = t('residuallabel') || 'Residual';
                if (response.data.residual !== undefined) weights[residualKey] = response.data.residual;
                
                const organicKey = t('organiclabel') || 'Organic';
                if (response.data.organic !== undefined) weights[organicKey] = response.data.organic;
                
                const glassKey = 'Glass';
                if (response.data.glass !== undefined) weights[glassKey] = response.data.glass;
                
                setWasteTypeWeights(weights);
            }
        } catch (error) {
            console.error('Error fetching weights:', error);
            setWasteTypeWeights({});
        }
    };

    fetchWasteTypeWeights();
  }, [id, dayvalue, isCustomDateRange, datevalue, t]);

  // --- API 2: FETCH SUBCLASS DATA ---
  useEffect(() => {
    const fetchSubclassData = async () => {
      try {
        setLoading(true);
        let binIdentifiers = Array.isArray(id) ? id : (typeof id === 'string' && id.includes(',') ? id.split(',') : [id]);
        binIdentifiers = binIdentifiers.map(id => typeof id === 'string' ? id.trim() : String(id)).filter(id => id.length > 0);

        const binResponse = await axiosInstance.get('/get_bins');
        const validBins = binIdentifiers.map(identifier => {
          let bin = binResponse.data.find(b => String(b.unique_identifier).toLowerCase() === String(identifier).toLowerCase());
          if (!bin && !isNaN(identifier)) bin = binResponse.data.find(b => b.id === parseInt(identifier));
          return bin;
        }).filter(b => b !== undefined);

        if (validBins.length === 0) throw new Error('No bins found');
        const binIds = validBins.map(bin => bin.id).join(',');

        let subclassUrl = `/subclass_waste_composition/${binIds}/${dayvalue.toLowerCase()}/`;
        const subclassParams = new URLSearchParams();

        if (isCustomDateRange && datevalue[0]?.startDate && datevalue[0]?.endDate) {
            const sDate = datevalue[0].startDate instanceof Date ? datevalue[0].startDate : new Date(datevalue[0].startDate);
            const eDate = datevalue[0].endDate instanceof Date ? datevalue[0].endDate : new Date(datevalue[0].endDate);
            if (!isNaN(sDate) && !isNaN(eDate)) {
                subclassParams.append('start_date', sDate.toISOString());
                subclassParams.append('end_date', eDate.toISOString());
            }
        }

        if (subclassParams.toString()) subclassUrl += `?${subclassParams.toString()}`;

        const subclassResponse = await axiosInstance.get(subclassUrl);
        
        if (!subclassResponse.data) {
            setSubclassData({});
            return;
        }

        const combinedData = {};
        Object.keys(subclassResponse.data).forEach(wasteType => {
            if (shouldFilterWasteType(wasteType)) return;
            const normalizedType = normalizeAndCombineWasteTypes(wasteType);
            if (!normalizedType) return;
            if (!combinedData[normalizedType]) combinedData[normalizedType] = {};

            Object.keys(subclassResponse.data[wasteType]).forEach(subClass => {
                if (shouldFilterWasteType(subClass)) return;
                const normalizedSubClass = normalizeAndFormatSubclassName(subClass);
                const value = subclassResponse.data[wasteType][subClass];
                if (normalizedSubClass && value > 0) {
                    combinedData[normalizedType][normalizedSubClass] = (combinedData[normalizedType][normalizedSubClass] || 0) + value;
                }
            });
        });

        setSubclassData(combinedData);

      } catch (err) {
        console.error('Error subclass data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSubclassData();
  }, [dayvalue, id, isCustomDateRange, datevalue, t]);

  // --- CALCULATION ---
  useEffect(() => {
    if (!subclassData) {
        setWasteData([]);
        return;
    }
    const calculatedItems = [];
    Object.keys(subclassData).forEach(categoryName => {
        const categoryItems = subclassData[categoryName];
        const categoryRawTotal = Object.values(categoryItems).reduce((sum, val) => sum + val, 0);
        const categoryRealWeight = wasteTypeWeights[categoryName] || 0;

        if (categoryRawTotal > 0 && categoryRealWeight > 0) {
            Object.keys(categoryItems).forEach(itemName => {
                const rawValue = categoryItems[itemName];
                const distributionPercentage = rawValue / categoryRawTotal;
                const itemRealWeight = distributionPercentage * categoryRealWeight;
                const displayPercentage = wasteweight > 0 ? (itemRealWeight / wasteweight) * 100 : 0;
                calculatedItems.push({
                    name: itemName,
                    weight: itemRealWeight,
                    percentage: displayPercentage,
                    wasteType: categoryName
                });
            });
        }
    });
    setWasteData(calculatedItems.sort((a, b) => b.weight - a.weight));
  }, [subclassData, wasteTypeWeights, wasteweight]);

  // --- FILTERING ---
  const availableCategories = useMemo(() => {
    const categories = new Set(wasteData.map(item => item.wasteType));
    // Sort, but make sure All is first
    const sorted = Array.from(categories).sort();
    return ['All', ...sorted];
  }, [wasteData]);

  const filteredWasteData = useMemo(() => {
    if (selectedCategory === 'All') return wasteData;
    return wasteData.filter(item => item.wasteType === selectedCategory);
  }, [wasteData, selectedCategory]);

  const filteredTotalWeight = useMemo(() => {
    return filteredWasteData.reduce((sum, item) => sum + item.weight, 0);
  }, [filteredWasteData]);


  // --- RENDER ---
  return (
    <div className={`flex flex-col h-full w-full ${className}`}>
      
      {/* HEADER */}
      <div className="flex-shrink-0 mb-2 px-4 pt-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
                {t('subwastecardlabel') || 'Sub-waste Heatmap'}
              </h4>
              <p className="text-xs text-gray-500 font-medium mt-0.5">
                {isCustomDateRange && datevalue?.[0]?.startDate && datevalue?.[0]?.endDate ? (
                  (() => {
                    const startDate = datevalue[0].startDate instanceof Date
                      ? datevalue[0].startDate
                      : new Date(datevalue[0].startDate);
                    const endDate = datevalue[0].endDate instanceof Date
                      ? datevalue[0].endDate
                      : new Date(datevalue[0].endDate);

                    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                      return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
                    }
                    return t('customRange');
                  })()
                ) : (
                  t(dayvalue.toLowerCase()) || dayvalue
                )}
              </p>
            </div>
            
            {/* Unit Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 border border-gray-200">
              <button onClick={() => setDisplayMode('kg')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${displayMode === 'kg' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>KG</button>
              <button onClick={() => setDisplayMode('percentage')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${displayMode === 'percentage' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>%</button>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="relative">
             <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 mask-linear-fade">
                {availableCategories.map(cat => (
                    <button
                        key={cat}
                        onClick={() => setSelectedCategory(cat)}
                        className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border ${
                            selectedCategory === cat 
                            ? 'bg-primary text-white border-primary shadow-md' 
                            : 'bg-white text-gray-600 border-gray-200 hover:text-primary hover:bg-gray-50'
                        }`}
                    >
                        {cat === 'All' ? <span className="flex items-center gap-1"><Layers className="w-3 h-3"/> {t('All') || 'All'}</span> : cat}
                    </button>
                ))}
             </div>
          </div>
        </div>
      </div>
      
      {/* CARD GRID */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-2 px-4 scrollbar-thin">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-red-500">
            {t('errorLoadingData') || 'Error loading data'}
          </div>
        ) : filteredWasteData.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[minmax(120px,auto)] gap-3 grid-flow-dense pb-2">
            {filteredWasteData.map((item, index) => {
              const cardSize = getCardSize(index);
              const isLarge = (selectedCategory === 'All' && index === 0);
              
              return (
                <div
                  key={`${item.wasteType}-${item.name}`}
                  className={`${item.colorClass || getColorClass(index)} ${cardSize} rounded-2xl p-4 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-2 shadow-sm">
                      {React.cloneElement(getIcon(item.name), { 
                        className: isLarge ? "w-8 h-8 text-gray-700" : "w-5 h-5 text-gray-700" 
                      })}
                    </div>
                    {/* Badge (shows unit NOT in main display) */}
                    <span className="text-[10px] font-bold bg-white/70 px-2 py-1 rounded-full text-gray-700">
                      {displayMode === 'kg' ? `${item.percentage.toFixed(1)}%` : `${item.weight.toFixed(2)} kg`}
                    </span>
                  </div>
                  
                  <div>
                    <h3 className={`font-semibold text-gray-800 leading-tight mb-0.5 ${isLarge ? 'text-lg' : 'text-sm'}`}>
                      {item.name}
                    </h3>
                    
                    {/* CONDITIONAL CATEGORY LABEL: Only show when viewing 'All' */}
                    {selectedCategory === 'All' && (
                        <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mb-1 opacity-80">
                            {item.wasteType}
                        </div>
                    )}

                    <div className="flex items-baseline gap-1 mt-1">
                      {displayMode === 'kg' ? (
                        <>
                          <span className={`font-bold text-gray-900 ${isLarge ? 'text-3xl' : 'text-xl'}`}>
                            {item.weight.toFixed(2)}
                          </span>
                          <span className="text-xs text-gray-600 font-medium">kg</span>
                        </>
                      ) : (
                        <>
                          <span className={`font-bold text-gray-900 ${isLarge ? 'text-3xl' : 'text-xl'}`}>
                            {item.percentage.toFixed(1)}
                          </span>
                          <span className="text-xs text-gray-600 font-medium">%</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-400 text-sm gap-2">
            <Layers className="w-8 h-8 opacity-20"/>
            <p>{t('noDataForCategory') || 'No items found in this category'}</p>
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div className="flex-shrink-0 pt-3 mt-auto border-t border-gray-100 px-4 pb-4 bg-white/50 backdrop-blur-sm">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-gray-500 font-bold">
              {selectedCategory === 'All' ? (t('total') || 'Total') : selectedCategory}:
            </span>
            <span className="text-lg font-extrabold text-primary">
              {filteredTotalWeight.toFixed(2)} <span className="text-sm font-medium text-gray-500">kg</span>
            </span>
          </div>
          <div className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
            {filteredWasteData.length} {t('items') || 'items'}
          </div>
        </div>
      </div>

    </div>
  );
};

export default SubclassCardView;